import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';

export const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Use auth endpoint for getting the user profile
          const response = await axios.get('/auth/me');
          setUser(response.data.data || response.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // Use auth endpoint for login
      const response = await axios.post('/auth/login', { email, password });
      const { token, ...userData } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(userData);
      setIsAuthenticated(true);

      toast.success('Login successful');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 'Login failed';
      toast.error(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const register = async (userData) => {
    try {
      // Use auth endpoint for registration
      const response = await axios.post('/auth/register', userData);
      const { token, ...newUserData } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(newUserData);
      setIsAuthenticated(true);

      toast.success('Registration successful');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const cleanMobile = (number) => {
    if (!number) return '';
    const digitsOnly = number.replace(/\D/g, '');
    if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
      return digitsOnly.slice(2);
    }
    return digitsOnly;
  };

  const generateOTP = async (mobileNumber) => {
    try {
      setError(null);
      const cleanedNumber = cleanMobile(mobileNumber);
      const response = await axios.post('/auth/signin-mobile', { mobileNumber: cleanedNumber });
      toast.success('OTP sent successfully');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send OTP';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };

  const verifyOTP = async (mobileNumber, otp) => {
    try {
      setError(null);
      const cleanedNumber = cleanMobile(mobileNumber);
      const response = await axios.post('/auth/signin-mobile-verify', { mobileNumber: cleanedNumber, otp });

      const { token, user: userData, refresh_token } = response.data;

      if (token) {
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      setUser(userData || response.data);
      setIsAuthenticated(true);

      toast.success('OTP verification successful');
      return { success: true, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'OTP verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    generateOTP,
    verifyOTP,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};